/**
 * Compact Referral Card Component - Trade Loop v2.0
 *
 * Displays referral information in a compact card format
 * Used in the referrals list view
 */

import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/contexts/AuthContext";
import { useState } from "react";
import { ChevronRight, Users } from "lucide-react";
import ReferralDetailModal from "@/components/ReferralDetailModal";

interface CompactReferralCardProps {
  referral: {
    id: number;
    senderId: string;
    receiverId: string;
    senderName?: string;
    senderTrade?: string;
    senderProfileImage?: string;
    receiverName?: string;
    receiverTrade?: string;
    receiverProfileImage?: string;
    status: string;
    clientName?: string;
    customerName?: string;
    clientEmail?: string;
    clientPhone?: string;
    createdAt: string;
    completedAt?: string;
    attachments?: string | null; // Field name from /list endpoint (inconsistent with other endpoints)
    notes?: string;
    jobDescription?: string;
    jobLocation?: string;
    trades?: string[];
    customTrades?: string[];
    // Availability request fields
    availabilityRequestId?: string | null;
    [key: string]: any;
  };
}

// Helper function for status badge styling
const getStatusBadgeStyle = (status: string) => {
  switch (status?.toLowerCase()) {
    case "pending":
      return "bg-yellow-100 text-yellow-700 border-yellow-200";
    case "accepted":
      return "bg-green-100 text-green-700 border-green-200";
    case "completed":
      return "bg-blue-100 text-blue-700 border-blue-200";
    case "declined":
      return "bg-red-100 text-red-700 border-red-200";
    case "closed":
      return "bg-gray-100 text-gray-600 border-gray-200";
    case "expired":
      return "bg-gray-100 text-gray-600 border-gray-200";
    case "canceled":
    case "cancelled":
      return "bg-orange-100 text-orange-700 border-orange-200";
    default:
      return "bg-gray-100 text-gray-600 border-gray-200";
  }
};

// Helper function for card background/border based on status
const getCardStyle = (status: string) => {
  switch (status?.toLowerCase()) {
    case "pending":
      return "bg-yellow-50 border-yellow-200 hover:bg-yellow-100 hover:border-yellow-300";
    case "accepted":
      return "bg-green-50 border-green-200 hover:bg-green-100 hover:border-green-300";
    case "completed":
      return "bg-blue-50 border-blue-200 hover:bg-blue-100 hover:border-blue-300";
    case "declined":
      return "bg-red-50 border-red-200 hover:bg-red-100 hover:border-red-300";
    case "closed":
      return "bg-gray-50 border-gray-200 hover:bg-gray-100 hover:border-gray-300";
    case "expired":
      return "bg-gray-50 border-gray-200 hover:bg-gray-100 hover:border-gray-300";
    case "canceled":
    case "cancelled":
      return "bg-orange-50 border-orange-200 hover:bg-orange-100 hover:border-orange-300";
    default:
      return "bg-white border-gray-200 hover:bg-gray-50 hover:border-gray-300";
  }
};

// Helper function for left border flag color (darker than card background)
const getLeftBorderColor = (status: string) => {
  switch (status?.toLowerCase()) {
    case "pending":
      return "bg-yellow-500";
    case "accepted":
      return "bg-green-500";
    case "completed":
      return "bg-blue-500";
    case "declined":
      return "bg-red-500";
    case "closed":
      return "bg-gray-400";
    case "expired":
      return "bg-gray-400";
    case "canceled":
    case "cancelled":
      return "bg-orange-500";
    default:
      return "bg-gray-400";
  }
};

export default function CompactReferralCard({ referral }: CompactReferralCardProps) {
  const { user } = useAuth();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleCardClick = () => {
    setIsModalOpen(true);
  };

  // Determine if this is a sent or received referral
  const currentUserId = user?.id;
  const isSent = referral.senderId === currentUserId;

  const formatTimestamp = (date: string) => {
    const created = new Date(date);
    return created.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  // Get client name
  const clientName = referral.clientName || referral.customerName || "Unnamed Referral";

  // Check if this is a group representative
  const isGroupRep = referral._isGroupRepresentative;
  const groupSize = referral._groupSize || 0;

  // Determine which status to display
  // For group referrals (sent by this user), show group status instead of individual status
  const displayStatus = referral.groupStatus || referral.status;
  const isShowingGroupStatus = !!referral.groupStatus;

  return (
    <>
      <div
        className={`flex items-center justify-between border rounded-lg transition-colors cursor-pointer group relative shadow-sm overflow-hidden ${getCardStyle(displayStatus)}`}
        onClick={handleCardClick}
      >
        {/* Left border flag */}
        <div className={`w-1 h-full absolute left-0 top-0 ${getLeftBorderColor(displayStatus)}`} />

        {/* Content with padding to account for left flag */}
        <div className="flex items-center justify-between w-full p-4 pl-5">
          {/* Left: Client Name + Job Type (or group info) */}
          <div className="flex-1 min-w-0">
          <p className="font-semibold text-gray-900 truncate">
            {clientName || 'Unnamed Referral'}
          </p>
          {isGroupRep ? (
            <p className="text-sm text-gray-500 truncate mt-0.5 flex items-center gap-1">
              <Users className="w-3.5 h-3.5" />
              Sent to {groupSize} partner{groupSize !== 1 ? 's' : ''}
            </p>
          ) : (
            ((referral.trades && referral.trades.length > 0) || (referral.customTrades && referral.customTrades.length > 0)) && (
              <p className="text-sm text-gray-500 truncate mt-0.5">
                {[
                  ...(referral.trades || []),
                  ...(referral.customTrades || [])
                ].join(', ')}
              </p>
            )
          )}
          {!isGroupRep && isSent && referral.receiverId && displayStatus?.toLowerCase() === 'pending' && (
            <p className="text-xs font-medium text-yellow-700 truncate mt-0.5">
              Awaiting response{referral.receiverFirstViewedAt ? ' · Viewed' : ''}
            </p>
          )}
        </div>

        {/* Right: badges (stacked) + date + menu + arrow */}
        <div className="flex items-center gap-2 flex-shrink-0 ml-3">
          <div className="flex flex-col items-end gap-1">
            {/* Sent/Received + Group + Status badges - stacked vertically */}
            <div className="flex flex-col items-end gap-1">
              <div className="flex items-center gap-1">
                <Badge className={isSent
                  ? "bg-blue-100 text-blue-700 border border-blue-200 text-xs px-1.5 py-0"
                  : "bg-green-100 text-green-700 border border-green-200 text-xs px-1.5 py-0"
                }>
                  {isSent ? "Sent" : "Received"}
                </Badge>
                {referral.referral_group_id && (
                  <Badge className="bg-purple-100 text-purple-700 border border-purple-200 text-xs px-1.5 py-0 flex items-center gap-1">
                    <Users className="w-3 h-3" />
                    Group
                  </Badge>
                )}
              </div>
              <Badge
                className={`${getStatusBadgeStyle(displayStatus)} text-xs px-1.5 py-0`}
                title={isShowingGroupStatus ? `Group Status: ${displayStatus}` : undefined}
              >
                {displayStatus?.charAt(0).toUpperCase() + displayStatus?.slice(1)}
              </Badge>
            </div>
            {/* Date */}
            <span className="text-xs text-gray-500 mt-0.5">
              {new Date(referral.createdAt || referral.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
            </span>
          </div>

          <ChevronRight className="w-7 h-7 text-gray-400 group-hover:text-gray-600 group-hover:translate-x-0.5 transition-all" />
        </div>
        </div>
      </div>

      {/* Referral Detail Modal */}
      <ReferralDetailModal
        referral={referral}
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      />
    </>
  );
}
